import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3 } = FiIcons;

const ComparisonChart = ({ results }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Monthly Payment Chart Options
  const monthlyPaymentOption = {
    title: {
      text: 'Monthly Payment\nComparison',
      left: 'center',
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#1f2937'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function (params) {
        let result = `<div style="font-weight: bold; margin-bottom: 8px;">Monthly Payment</div>`;
        params.forEach(param => {
          const value = formatCurrency(param.value);
          result += `
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px; border-radius: 50%;"></span>
              <span style="margin-right: 8px;">${param.seriesName}:</span>
              <span style="font-weight: bold;">${value}</span>
            </div>
          `;
        });
        return result;
      }
    },
    legend: {
      data: ['Current', 'Refinance'],
      top: 30,
      textStyle: {
        fontSize: 10,
        color: '#6b7280'
      }
    },
    grid: {
      left: '10%',
      right: '10%',
      bottom: '20%',
      top: '35%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: ['Payment'],
      axisLabel: {
        color: '#6b7280',
        fontSize: 10,
        fontWeight: 500
      },
      axisLine: {
        lineStyle: {
          color: '#e5e7eb'
        }
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 10000,
      axisLabel: {
        formatter: function (value) {
          return formatCurrency(value);
        },
        color: '#6b7280',
        fontSize: 9
      },
      axisLine: {
        lineStyle: {
          color: '#e5e7eb'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f3f4f6'
        }
      }
    },
    series: [
      {
        name: 'Current',
        type: 'bar',
        data: [results.current.monthlyPayment],
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: '#3b82f6'
            }, {
              offset: 1, color: '#1d4ed8'
            }]
          },
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(59, 130, 246, 0.3)'
          }
        }
      },
      {
        name: 'Refinance',
        type: 'bar',
        data: [results.refi.monthlyPayment],
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: '#10b981'
            }, {
              offset: 1, color: '#047857'
            }]
          },
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(16, 185, 129, 0.3)'
          }
        }
      }
    ],
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut'
  };

  // Total Interest Chart Options
  const totalInterestOption = {
    title: {
      text: 'Total Interest\nComparison',
      left: 'center',
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#1f2937'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function (params) {
        let result = `<div style="font-weight: bold; margin-bottom: 8px;">Total Interest</div>`;
        params.forEach(param => {
          const value = formatCurrency(param.value);
          result += `
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px; border-radius: 50%;"></span>
              <span style="margin-right: 8px;">${param.seriesName}:</span>
              <span style="font-weight: bold;">${value}</span>
            </div>
          `;
        });
        return result;
      }
    },
    legend: {
      data: ['Current', 'Refinance'],
      top: 30,
      textStyle: {
        fontSize: 10,
        color: '#6b7280'
      }
    },
    grid: {
      left: '10%',
      right: '10%',
      bottom: '20%',
      top: '35%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: ['Interest'],
      axisLabel: {
        color: '#6b7280',
        fontSize: 10,
        fontWeight: 500
      },
      axisLine: {
        lineStyle: {
          color: '#e5e7eb'
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: function (value) {
          return formatCurrency(value);
        },
        color: '#6b7280',
        fontSize: 9
      },
      axisLine: {
        lineStyle: {
          color: '#e5e7eb'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f3f4f6'
        }
      }
    },
    series: [
      {
        name: 'Current',
        type: 'bar',
        data: [results.current.totalInterest],
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: '#3b82f6'
            }, {
              offset: 1, color: '#1d4ed8'
            }]
          },
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(59, 130, 246, 0.3)'
          }
        }
      },
      {
        name: 'Refinance',
        type: 'bar',
        data: [results.refi.totalInterest],
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: '#10b981'
            }, {
              offset: 1, color: '#047857'
            }]
          },
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(16, 185, 129, 0.3)'
          }
        }
      }
    ],
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100"
    >
      <div className="flex items-center gap-3 mb-6">
        <SafeIcon icon={FiBarChart3} className="text-2xl text-indigo-600" />
        <h3 className="text-xl font-bold text-gray-900">Visual Comparison</h3>
      </div>
      
      {/* Side-by-side Charts */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Monthly Payment Chart */}
        <div className="h-64">
          <ReactECharts 
            option={monthlyPaymentOption} 
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </div>

        {/* Total Interest Chart */}
        <div className="h-64">
          <ReactECharts 
            option={totalInterestOption} 
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-100">
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-1">Monthly Difference</p>
          <p className={`text-lg font-bold ${results.monthlyDifference < 0 ? 'text-success-600' : 'text-warning-600'}`}>
            {results.monthlyDifference < 0 ? '-' : '+'}{formatCurrency(Math.abs(results.monthlyDifference))}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-1">Total Interest Difference</p>
          <p className={`text-lg font-bold ${results.totalInterestDifference < 0 ? 'text-success-600' : 'text-warning-600'}`}>
            {results.totalInterestDifference < 0 ? '-' : '+'}{formatCurrency(Math.abs(results.totalInterestDifference))}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default ComparisonChart;